import React from 'react'
import { Button } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { AlertModal } from '@/shared/ui/AlertModal'
import CouponFormModal from '../components/CouponFormModal'
import CouponTable from '../components/CouponTable'
import { deleteCoupon, listCoupons, listAffiliatesBasic } from '../services/couponsService'
import type { Coupon } from '../types'

export default function CouponsAdminPage() {
  const { add: toast } = useToast()
  const [items, setItems] = React.useState<Coupon[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [openForm, setOpenForm] = React.useState(false)
  const [current, setCurrent] = React.useState<Coupon | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false)
  const [affiliatesById, setAffiliatesById] = React.useState<Record<string, { name?: string|null; email?: string|null; code?: string|null }>>({})

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listCoupons()
      setItems(list)
    } catch (e:any) { setError(e?.message || 'Falha ao carregar cupons') }
    finally { setLoading(false) }
  }, [])

  React.useEffect(() => { load() }, [load])

  React.useEffect(() => { (async () => { try {
    const list = await listAffiliatesBasic()
    const map: Record<string, { name?: string|null; email?: string|null; code?: string|null }> = {}
    for (const a of list) map[a.id] = { name: a.name, email: a.email, code: a.code }
    setAffiliatesById(map)
  } catch {} })() }, [])

  function onCreate() { setCurrent(null); setOpenForm(true) }
  function onEdit(c: Coupon) { setCurrent(c); setOpenForm(true) }
  function onDelete(c: Coupon) { setCurrent(c); setConfirmDeleteOpen(true) }

  async function handleConfirmDelete() {
    if (!current) return
    try {
      await deleteCoupon(current.id)
      await load()
      toast({ title: 'Cupom excluído', description: `${current.code || current.name || 'Cupom'} removido com sucesso.`, variant: 'success', soft: true })
    } catch (e:any) {
      const msg = e?.message || 'Falha ao excluir'
      setError(msg)
      toast({ title: 'Falha ao excluir', description: msg, variant: 'error', soft: true })
    } finally { setConfirmDeleteOpen(false) }
  }

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <CouponFormModal
        open={openForm}
        onClose={()=> setOpenForm(false)}
        onSaved={async ()=> { await load() }}
        initial={current ?? undefined}
      />
      <div className="mb-6">
        <div className="text-2xl font-semibold">Cupom de Desconto</div>
        <div className="text-sm text-foreground/70">Gerencie cupons para campanhas e promoções.</div>
      </div>
      {error && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
      <div className="mb-3 flex items-center justify-between">
        <div />
        <Button onClick={onCreate}>Novo cupom</Button>
      </div>
      {loading ? (
        <div className="rounded-md border border-border p-4 text-sm text-foreground/70">Carregando...</div>
      ) : (
        <CouponTable items={items} onEdit={onEdit} onDelete={onDelete} affiliatesById={affiliatesById} />
      )}
      <AlertModal
        open={confirmDeleteOpen}
        onClose={()=> setConfirmDeleteOpen(false)}
        title="Excluir cupom"
        variant="error"
        description={current ? (
          <span>Tem certeza que deseja excluir o cupom <span className="font-mono">{current.code}</span>?</span>
        ) : 'Tem certeza que deseja excluir este cupom?'}
        confirmText="Excluir"
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

